import React, { useEffect, useState } from 'react';
import { getQuotations, createQuotation, updateQuotation, approveQuotation, sendQuotation, reviseQuotation, acceptQuotation, rejectQuotation, convertQuotationToContract, createClientPOFromQuotation, getCompanies, getSuppliers, getAssets, submitQuotationForApproval, approveQuotationWorkflow, rejectQuotationInternal, getMyScopedQuotations } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import { FileCheck, Plus, Send, CheckCircle2, XCircle, AlertTriangle, Eye, Edit, FileText, ArrowRight, Truck, Calendar, MoreVertical, ShoppingCart } from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '../components/ui/dropdown-menu';
import { toast } from 'sonner';
import { useLocation, useNavigate } from 'react-router-dom';

// New status workflow per spec: Draft -> Sent -> Revised -> Accepted -> Rejected
const statusConfig = {
  draft: { color: 'bg-gray-100 text-gray-800', label: 'Draft' },
  sent: { color: 'bg-blue-100 text-blue-800', label: 'Sent to Client' },
  revised: { color: 'bg-amber-100 text-amber-800', label: 'Revised' },
  accepted: { color: 'bg-emerald-100 text-emerald-800', label: 'Accepted' },
  rejected: { color: 'bg-red-100 text-red-800', label: 'Rejected' },
  // Legacy statuses for backwards compatibility
  pending_approval: { color: 'bg-amber-100 text-amber-800', label: 'Pending Approval' },
  rejected_internal: { color: 'bg-red-100 text-red-800', label: 'Rejected (Internal)' },
  approved: { color: 'bg-green-100 text-green-800', label: 'Approved' },
  sent_to_client: { color: 'bg-blue-100 text-blue-800', label: 'Sent to Client' },
  declined: { color: 'bg-red-100 text-red-800', label: 'Declined' },
};

export const QuotationsPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isManager = ['admin', 'sales_manager', 'manager'].includes(user?.role);
  const enquiry = location.state?.enquiry;
  
  const [quotations, setQuotations] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(!!enquiry);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [convertDialogOpen, setConvertDialogOpen] = useState(false);
  const [createPODialogOpen, setCreatePODialogOpen] = useState(false);
  const [reviseDialogOpen, setReviseDialogOpen] = useState(false);
  const [selectedQuotation, setSelectedQuotation] = useState(null);
  const [items, setItems] = useState([]);
  const [reviseItems, setReviseItems] = useState([]);
  const [reviseSubmitting, setReviseSubmitting] = useState(false);
  const [availableAssets, setAvailableAssets] = useState([]);
  const [contractStartDate, setContractStartDate] = useState('');
  const [poNumber, setPONumber] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [exporting, setExporting] = useState(false);
  const isAdmin = user?.role === 'admin';
  
  const [formData, setFormData] = useState({
    enquiry_id: enquiry?.id || '',
    company_id: enquiry?.company_id || '',
    client_name: enquiry?.client_name || '',
    client_email: enquiry?.client_email || '',
    client_phone: enquiry?.client_phone || '',
    valid_days: 30,
    notes: '',
    terms_and_conditions: ''
  });

  const [currentItem, setCurrentItem] = useState({
    asset_type: enquiry?.asset_type || '',
    description: '',
    quantity: enquiry?.quantity || 1,
    rate_per_unit: 0,
    supplier_cost: 0,
    is_from_own_fleet: true,
    with_driver: enquiry?.with_driver || false,
    driver_rate: 0,
    mobilization_cost: 0
  });

  useEffect(() => {
    loadCompanies();
    loadSuppliers();
    loadQuotations();
  }, []);

  useEffect(() => {
    if (formData.company_id && currentItem.asset_type) {
      checkOwnFleet();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.company_id, currentItem.asset_type]);

  const loadCompanies = async () => {
    try {
      const response = await getCompanies();
      setCompanies(response.data);
    } catch (error) {
      console.error('Failed to load companies:', error);
    }
  };

  const loadSuppliers = async () => {
    try {
      const response = await getSuppliers();
      setSuppliers(response.data);
    } catch (error) {
      console.error('Failed to load suppliers:', error);
    }
  };

  const loadQuotations = async () => {
    setLoading(true);
    try {
      const status = statusFilter && statusFilter !== 'all' ? statusFilter : null;
      // Sales users get scoped list (only their own quotations); other roles see all
      const response = user?.role === 'sales'
        ? await getMyScopedQuotations(null, status)
        : await getQuotations(null, status);
      setQuotations(response.data);
    } catch (error) {
      toast.error('Failed to load quotations');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadQuotations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  const handleExport = async (format) => {
    setExporting(true);
    try {
      const params = statusFilter && statusFilter !== 'all' ? `?status=${statusFilter}` : '';
      const api = (await import('../lib/api')).default;
      const url = `/quotations-export/${format}${params}`;
      const response = await api.get(url, { responseType: 'blob' });
      const blob = new Blob([response.data], {
        type: format === 'xlsx'
          ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
          : 'application/pdf',
      });
      const dl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = dl;
      a.download = `quotations_${new Date().toISOString().slice(0, 10)}.${format}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(dl);
      toast.success(`Exported ${format.toUpperCase()}`);
    } catch (error) {
      toast.error('Export failed');
    } finally {
      setExporting(false);
    }
  };

  const checkOwnFleet = async () => {
    try {
      const response = await getAssets(formData.company_id, 'available');
      const matching = response.data.filter(a => 
        a.asset_type.toLowerCase().includes(currentItem.asset_type.toLowerCase())
      );
      setAvailableAssets(matching);
    } catch (error) {
      console.error('Failed to check fleet:', error);
    }
  };

  const calculatePricing = () => {
    if (currentItem.is_from_own_fleet) {
      if (availableAssets.length > 0) {
        const avgEMI = availableAssets.reduce((sum, a) => sum + (a.monthly_emi || 0), 0) / availableAssets.length;
        const suggestedRate = Math.round(avgEMI * 1.25);
        setCurrentItem(prev => ({ ...prev, rate_per_unit: suggestedRate, supplier_cost: avgEMI }));
      }
    } else {
      if (currentItem.supplier_cost > 0) {
        const suggestedRate = Math.round(currentItem.supplier_cost * 1.25);
        setCurrentItem(prev => ({ ...prev, rate_per_unit: suggestedRate }));
      }
    }
  };

  useEffect(() => {
    calculatePricing();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentItem.is_from_own_fleet, currentItem.supplier_cost, availableAssets.length]);

  const addItem = () => {
    if (!currentItem.asset_type || currentItem.rate_per_unit <= 0) {
      toast.error('Please fill all item details');
      return;
    }

    // Calculate item total including driver rate
    let itemTotal = currentItem.rate_per_unit * currentItem.quantity;
    if (currentItem.with_driver && currentItem.driver_rate > 0) {
      itemTotal += currentItem.driver_rate * currentItem.quantity;
    }
    
    const supplierTotal = currentItem.supplier_cost * currentItem.quantity;
    const profitAmount = itemTotal - supplierTotal;
    const profitMargin = (profitAmount / itemTotal) * 100;

    if (profitMargin < 10) {
      toast.warning('Item margin is below 10% — this quotation will require manager approval');
    }

    setItems([...items, { ...currentItem, total: itemTotal }]);
    setCurrentItem({
      asset_type: '',
      description: '',
      quantity: 1,
      rate_per_unit: 0,
      supplier_cost: 0,
      is_from_own_fleet: true,
      with_driver: false,
      driver_rate: 0,
      mobilization_cost: 0
    });
  };

  const removeItem = (index) => {
    setItems(items.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (items.length === 0) {
      toast.error('Please add at least one item');
      return;
    }

    try {
      const response = await createQuotation({ ...formData, items });
      const created = response.data;
      if (created?.profit_margin < 10) {
        try {
          await submitQuotationForApproval(created.id);
          toast.warning('Quotation created with margin below 10% — submitted for manager approval');
        } catch (err) {
          toast.error('Quotation created but failed to submit for approval');
        }
      } else {
        toast.success('Quotation created successfully!');
      }
      setDialogOpen(false);
      loadQuotations();
      setItems([]);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create quotation');
    }
  };

  const handleManagerApprove = async (id) => {
    try {
      await approveQuotationWorkflow(id, { notes: 'Approved by manager' });
      toast.success('Low-margin quotation approved — it can now be sent to the client');
      loadQuotations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve quotation');
    }
  };

  const handleManagerReject = async (id) => {
    const reason = window.prompt('Reason for internal rejection:');
    if (!reason) return;
    try {
      await rejectQuotationInternal(id, { reason });
      toast.success('Quotation rejected internally');
      loadQuotations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject quotation');
    }
  };

  const handleApprove = async (id) => {
    try {
      await approveQuotation(id);
      toast.success('Quotation approved!');
      loadQuotations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve quotation');
    }
  };

  const handleSend = async (id) => {
    try {
      await sendQuotation(id);
      toast.success('Quotation sent to client!');
      loadQuotations();
    } catch (error) {
      toast.error('Failed to send quotation');
    }
  };

  const openReviseDialog = (quotation) => {
    setSelectedQuotation(quotation);
    // Pre-populate items from the quotation
    const itemsCopy = (quotation.items || []).map((item, idx) => ({
      ...item,
      key: idx,
      rate_per_unit: item.rate_per_unit || 0,
      supplier_cost: item.supplier_cost || 0,
      quantity: item.quantity || 1,
      total: item.total || 0,
      driver_rate: item.driver_rate || 0,
      mobilization_cost: item.mobilization_cost || 0,
    }));
    setReviseItems(itemsCopy);
    setReviseDialogOpen(true);
  };

  const updateReviseItem = (index, field, value) => {
    setReviseItems(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      
      // Recalculate total
      const item = updated[index];
      let itemTotal = (item.rate_per_unit || 0) * (item.quantity || 1);
      if (item.with_driver && item.driver_rate > 0) {
        itemTotal += item.driver_rate * (item.quantity || 1);
      }
      updated[index].total = itemTotal;
      
      return updated;
    });
  };

  const handleReviseSubmit = async () => {
    if (!selectedQuotation || reviseItems.length === 0) return;
    
    setReviseSubmitting(true);
    try {
      // Step 1: Create revision copy
      const reviseResponse = await reviseQuotation(selectedQuotation.id);
      const newQuotationId = reviseResponse.data.id;
      
      // Step 2: Update the new quotation with revised items
      const updatePayload = {
        items: reviseItems.map(item => ({
          asset_type: item.asset_type,
          description: item.description || '',
          quantity: item.quantity || 1,
          rate_per_unit: parseFloat(item.rate_per_unit) || 0,
          supplier_cost: parseFloat(item.supplier_cost) || 0,
          is_from_own_fleet: item.is_from_own_fleet ?? true,
          with_driver: item.with_driver ?? false,
          driver_rate: parseFloat(item.driver_rate) || 0,
          mobilization_cost: parseFloat(item.mobilization_cost) || 0,
          total: item.total || 0,
        }))
      };
      
      await updateQuotation(newQuotationId, updatePayload);
      
      toast.success(`Revision ${reviseResponse.data.quotation_number} created with updated rates!`);
      setReviseDialogOpen(false);
      setSelectedQuotation(null);
      setReviseItems([]);
      loadQuotations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create revision');
    } finally {
      setReviseSubmitting(false);
    }
  };

  // Legacy function for backward compatibility if needed elsewhere
  const handleRevise = async (quotation) => {
    openReviseDialog(quotation);
  };

  const handleAccept = async (id) => {
    try {
      await acceptQuotation(id);
      toast.success('Quotation marked as accepted! You can now create a Client PO.');
      loadQuotations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to accept quotation');
    }
  };

  const handleReject = async (id) => {
    try {
      await rejectQuotation(id);
      toast.success('Quotation marked as rejected');
      loadQuotations();
    } catch (error) {
      toast.error('Failed to reject quotation');
    }
  };

  const handleConvertToContract = async () => {
    if (!contractStartDate) {
      toast.error('Please select a start date');
      return;
    }
    try {
      await convertQuotationToContract(selectedQuotation.id, contractStartDate);
      toast.success('Contract created! Operations team notified.');
      setConvertDialogOpen(false);
      loadQuotations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to convert to contract');
    }
  };

  const openCreatePODialog = (quotation) => {
    setSelectedQuotation(quotation);
    // Generate suggested PO number
    const now = new Date();
    setPONumber(`CPO-${now.getFullYear()}${(now.getMonth()+1).toString().padStart(2, '0')}-${Math.random().toString(36).substring(2, 6).toUpperCase()}`);
    setCreatePODialogOpen(true);
  };

  const handleCreateClientPO = async () => {
    if (!selectedQuotation) return;
    
    try {
      const result = await createClientPOFromQuotation(selectedQuotation.id, {
        po_number: poNumber
      });
      toast.success(`Client PO ${result.data.po_number} created successfully!`);
      setCreatePODialogOpen(false);
      // Navigate to Client PO page
      navigate('/client-pos');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create Client PO');
    }
  };

  const handleView = (quotation) => {
    setSelectedQuotation(quotation);
    setViewDialogOpen(true);
  };

  const openConvertDialog = (quotation) => {
    setSelectedQuotation(quotation);
    setContractStartDate(new Date().toISOString().split('T')[0]);
    setConvertDialogOpen(true);
  };

  const subtotal = items.reduce((sum, item) => sum + item.total, 0);
  const mobilizationTotal = items.reduce((sum, item) => sum + (item.mobilization_cost || 0), 0);
  const totalCost = items.reduce((sum, item) => sum + (item.supplier_cost * item.quantity), 0);
  const profitAmount = subtotal - totalCost;
  const profitMargin = subtotal > 0 ? (profitAmount / subtotal) * 100 : 0;

  return (
    <div className="space-y-6" data-testid="quotations-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold tracking-tight" data-testid="quotations-title">Quotations</h1>
          <p className="text-muted-foreground mt-1">{quotations.length} total quotations</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {isAdmin && (
            <>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleExport('xlsx')}
                disabled={exporting}
                data-testid="export-xlsx-button"
              >
                <FileText className="h-4 w-4 mr-1" /> Excel
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleExport('pdf')}
                disabled={exporting}
                data-testid="export-pdf-button"
              >
                <FileText className="h-4 w-4 mr-1" /> PDF
              </Button>
            </>
          )}
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-quotation-button">
              <Plus className="h-4 w-4 mr-2" />
              New Quotation
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Create Quotation</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label>Company *</Label>
                  <Select value={formData.company_id} onValueChange={(v) => setFormData({ ...formData, company_id: v })} required>
                    <SelectTrigger data-testid="quotation-company-select">
                      <SelectValue placeholder="Select company" />
                    </SelectTrigger>
                    <SelectContent>
                      {companies.map((c) => (
                        <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Client Name *</Label>
                  <Input value={formData.client_name} onChange={(e) => setFormData({ ...formData, client_name: e.target.value })} required data-testid="client-name-input" />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label>Client Email</Label>
                  <Input type="email" value={formData.client_email} onChange={(e) => setFormData({ ...formData, client_email: e.target.value })} data-testid="client-email-input" />
                </div>
                <div>
                  <Label>Client Phone</Label>
                  <Input value={formData.client_phone || ''} onChange={(e) => setFormData({ ...formData, client_phone: e.target.value })} placeholder="+966..." />
                </div>
              </div>

              {/* Add Items Section */}
              <div className="border rounded-lg p-4 bg-slate-50">
                <h3 className="font-semibold mb-3">Add Line Items</h3>
                <div className="space-y-3">
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                    <div>
                      <Label>Asset Type</Label>
                      <Input 
                        value={currentItem.asset_type} 
                        onChange={(e) => setCurrentItem({ ...currentItem, asset_type: e.target.value })} 
                        placeholder="Pickup, Excavator"
                        data-testid="item-asset-type"
                      />
                      {availableAssets.length > 0 && (
                        <p className="text-xs text-green-600 mt-1">{availableAssets.length} in fleet</p>
                      )}
                    </div>
                    <div>
                      <Label>Description</Label>
                      <Input 
                        value={currentItem.description} 
                        onChange={(e) => setCurrentItem({ ...currentItem, description: e.target.value })} 
                        placeholder="Optional details"
                      />
                    </div>
                    <div>
                      <Label>Quantity</Label>
                      <Input 
                        type="number" 
                        min="1"
                        value={currentItem.quantity} 
                        onChange={(e) => setCurrentItem({ ...currentItem, quantity: parseInt(e.target.value) || 1 })} 
                      />
                    </div>
                    <div className="flex items-center gap-2 pt-6">
                      <input 
                        type="checkbox" 
                        id="own_fleet" 
                        checked={currentItem.is_from_own_fleet} 
                        onChange={(e) => setCurrentItem({ ...currentItem, is_from_own_fleet: e.target.checked })} 
                      />
                      <Label htmlFor="own_fleet">Own Fleet</Label>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                    <div>
                      <Label>Supplier/Cost (Monthly)</Label>
                      <Input 
                        type="number" 
                        value={currentItem.supplier_cost} 
                        onChange={(e) => setCurrentItem({ ...currentItem, supplier_cost: parseFloat(e.target.value) || 0 })} 
                        placeholder="Base cost"
                      />
                    </div>
                    <div>
                      <Label>Rate per Unit (Monthly)</Label>
                      <Input 
                        type="number" 
                        value={currentItem.rate_per_unit} 
                        onChange={(e) => setCurrentItem({ ...currentItem, rate_per_unit: parseFloat(e.target.value) || 0 })} 
                        placeholder="Client rate"
                      />
                    </div>
                    <div>
                      <Label>Mobilization Cost</Label>
                      <Input 
                        type="number" 
                        value={currentItem.mobilization_cost} 
                        onChange={(e) => setCurrentItem({ ...currentItem, mobilization_cost: parseFloat(e.target.value) || 0 })} 
                        placeholder="One-time"
                      />
                    </div>
                    <div className="flex items-center gap-2 pt-6">
                      <input 
                        type="checkbox" 
                        id="with_driver" 
                        checked={currentItem.with_driver} 
                        onChange={(e) => setCurrentItem({ ...currentItem, with_driver: e.target.checked })} 
                      />
                      <Label htmlFor="with_driver">With Driver</Label>
                    </div>
                  </div>

                  {currentItem.with_driver && (
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                      <div>
                        <Label>Driver Rate (Monthly)</Label>
                        <Input 
                          type="number" 
                          value={currentItem.driver_rate} 
                          onChange={(e) => setCurrentItem({ ...currentItem, driver_rate: parseFloat(e.target.value) || 0 })} 
                          placeholder="Driver cost"
                        />
                      </div>
                    </div>
                  )}

                  <div className="flex justify-between items-center pt-2">
                    {currentItem.supplier_cost > 0 && currentItem.rate_per_unit > 0 && (
                      <p className={`text-sm ${((currentItem.rate_per_unit - currentItem.supplier_cost) / currentItem.rate_per_unit * 100) >= 10 ? 'text-green-600' : 'text-red-600'}`}>
                        Margin: {((currentItem.rate_per_unit - currentItem.supplier_cost) / currentItem.rate_per_unit * 100).toFixed(1)}%
                      </p>
                    )}
                    <Button type="button" onClick={addItem} data-testid="add-item-button">Add Item</Button>
                  </div>
                </div>
              </div>

              {/* Items List */}
              {items.length > 0 && (
                <div className="border rounded-lg p-4">
                  <h3 className="font-semibold mb-3">Line Items ({items.length})</h3>
                  <div className="space-y-2">
                    {items.map((item, index) => (
                      <div key={index} className="flex items-center justify-between p-3 bg-white rounded border">
                        <div className="flex-1">
                          <div className="font-semibold">{item.asset_type} {item.description && `- ${item.description}`}</div>
                          <div className="text-sm text-muted-foreground">
                            {item.quantity} x {item.rate_per_unit.toLocaleString()} SAR = {item.total.toLocaleString()} SAR
                            {item.is_from_own_fleet && <span className="ml-2 text-xs text-green-600">(Own Fleet)</span>}
                            {item.with_driver && <span className="ml-2 text-xs text-blue-600">(+Driver)</span>}
                            {item.mobilization_cost > 0 && <span className="ml-2 text-xs text-purple-600">(+Mob: {item.mobilization_cost} SAR)</span>}
                          </div>
                        </div>
                        <Button type="button" variant="ghost" size="sm" onClick={() => removeItem(index)}>
                          <XCircle className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                  <div className="mt-4 pt-4 border-t space-y-2">
                    <div className="flex justify-between text-sm">
                      <span>Subtotal:</span>
                      <span className="font-semibold">{subtotal.toLocaleString()} SAR</span>
                    </div>
                    {mobilizationTotal > 0 && (
                      <div className="flex justify-between text-sm">
                        <span>Mobilization:</span>
                        <span>{mobilizationTotal.toLocaleString()} SAR</span>
                      </div>
                    )}
                    <div className="flex justify-between text-sm">
                      <span>VAT (15%):</span>
                      <span>{((subtotal + mobilizationTotal) * 0.15).toLocaleString()} SAR</span>
                    </div>
                    <div className="flex justify-between font-bold">
                      <span>Total:</span>
                      <span>{((subtotal + mobilizationTotal) * 1.15).toLocaleString()} SAR</span>
                    </div>
                    <div className="flex justify-between border-t pt-2">
                      <span className="font-semibold">Profit:</span>
                      <div className="text-right">
                        <div className="font-bold text-green-600">{profitAmount.toLocaleString()} SAR</div>
                        <div className={`text-sm ${profitMargin >= 10 ? 'text-green-600' : 'text-red-600'}`}>
                          {profitMargin.toFixed(1)}% margin
                        </div>
                      </div>
                    </div>
                    {profitMargin < 10 && (
                      <div className="text-xs text-amber-600 flex items-center gap-1">
                        <AlertTriangle className="h-3 w-3" />
                        Margin below 10% — quotation will require manager approval before sending
                      </div>
                    )}
                  </div>
                </div>
              )}

              <div>
                <Label>Notes</Label>
                <Textarea 
                  value={formData.notes} 
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })} 
                  placeholder="Internal notes..."
                  rows={2}
                />
              </div>

              <div className="flex justify-end gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
                <Button type="submit" disabled={items.length === 0} data-testid="submit-quotation-button">
                  {profitMargin < 10 && items.length > 0 ? 'Create (Needs Approval)' : 'Create Quotation'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Status filter tabs - visible to sales & admin */}
      <div className="flex items-center gap-2 flex-wrap" data-testid="quotation-status-filters">
        {[
          { v: 'all', label: 'All' },
          { v: 'draft', label: 'Draft' },
          { v: 'pending_approval', label: 'Pending Approval' },
          { v: 'sent', label: 'Pending Client Response' },
          { v: 'revised', label: 'Revised' },
          { v: 'accepted', label: 'Accepted' },
          { v: 'rejected', label: 'Rejected' },
        ].map((f) => {
          const active = statusFilter === f.v;
          return (
            <Button
              key={f.v}
              variant={active ? 'default' : 'outline'}
              size="sm"
              onClick={() => setStatusFilter(f.v)}
              data-testid={`filter-${f.v}`}
            >
              {f.label}
            </Button>
          );
        })}
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="text-muted-foreground">Loading quotations...</div>
        </div>
      ) : quotations.length === 0 ? (
        <Card className="p-12 text-center">
          <FileCheck className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
          <h3 className="text-lg font-semibold mb-2">No quotations yet</h3>
          <p className="text-muted-foreground mb-4">Create your first quotation</p>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {quotations.map((quotation) => (
            <Card key={quotation.id} className="p-6 hover:shadow-lg transition-shadow duration-200" data-testid={`quotation-card-${quotation.id}`}>
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className="font-bold text-lg">{quotation.quotation_number}</h3>
                  <p className="text-sm text-muted-foreground">{quotation.client_name}</p>
                  {quotation.revision_number > 1 && (
                    <Badge variant="outline" className="mt-1 text-xs">Rev {quotation.revision_number}</Badge>
                  )}
                </div>
                <Badge className={statusConfig[quotation.status]?.color}>
                  {statusConfig[quotation.status]?.label}
                </Badge>
              </div>
              <div className="space-y-2 text-sm mb-4">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Total:</span>
                  <span className="font-bold text-lg">{quotation.total.toLocaleString()} SAR</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Profit Margin:</span>
                  <span className={quotation.profit_margin >= 10 ? 'text-green-600 font-semibold' : 'text-red-600 font-semibold'}>
                    {quotation.profit_margin.toFixed(1)}%
                  </span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-muted-foreground">Valid Until:</span>
                  <span>{new Date(quotation.valid_until).toLocaleDateString()}</span>
                </div>
              </div>
              
              <div className="flex gap-2">
                <Button size="sm" variant="outline" className="flex-1" onClick={() => handleView(quotation)}>
                  <Eye className="h-4 w-4 mr-1" />
                  View
                </Button>
                
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button size="sm" variant="outline">
                      <MoreVertical className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    {/* Pending Approval: Manager can approve/reject */}
                    {quotation.status === 'pending_approval' && (
                      isManager ? (
                        <>
                          <DropdownMenuItem onClick={() => handleManagerApprove(quotation.id)} data-testid={`approve-low-margin-${quotation.id}`}>
                            <CheckCircle2 className="h-4 w-4 mr-2 text-green-600" />
                            Approve (Low Margin)
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleManagerReject(quotation.id)} data-testid={`reject-low-margin-${quotation.id}`}>
                            <XCircle className="h-4 w-4 mr-2 text-red-600" />
                            Reject Internally
                          </DropdownMenuItem>
                        </>
                      ) : (
                        <DropdownMenuItem disabled>
                          <AlertTriangle className="h-4 w-4 mr-2 text-amber-600" />
                          Awaiting Manager Approval
                        </DropdownMenuItem>
                      )
                    )}
                    {/* Draft status: Can Send to Client */}
                    {quotation.status === 'draft' && (
                      <DropdownMenuItem onClick={() => handleSend(quotation.id)}>
                        <Send className="h-4 w-4 mr-2" />
                        Send to Client
                      </DropdownMenuItem>
                    )}
                    {/* Legacy: approved status maps to send */}
                    {quotation.status === 'approved' && (
                      <DropdownMenuItem onClick={() => handleSend(quotation.id)}>
                        <Send className="h-4 w-4 mr-2" />
                        Send to Client
                      </DropdownMenuItem>
                    )}
                    {/* Sent status: Can Accept or Reject */}
                    {(quotation.status === 'sent' || quotation.status === 'sent_to_client') && (
                      <>
                        <DropdownMenuItem onClick={() => handleAccept(quotation.id)}>
                          <CheckCircle2 className="h-4 w-4 mr-2 text-green-600" />
                          Mark Accepted
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleReject(quotation.id)}>
                          <XCircle className="h-4 w-4 mr-2 text-red-600" />
                          Mark Rejected
                        </DropdownMenuItem>
                      </>
                    )}
                    {/* Accepted status: Can Create Client PO */}
                    {quotation.status === 'accepted' && !quotation.client_po_id && (
                      <DropdownMenuItem onClick={() => openCreatePODialog(quotation)} className="text-emerald-700 font-semibold">
                        <ShoppingCart className="h-4 w-4 mr-2" />
                        Create Client PO
                      </DropdownMenuItem>
                    )}
                    {/* Show linked Client PO if exists */}
                    {quotation.client_po_id && (
                      <DropdownMenuItem onClick={() => navigate('/client-pos')} className="text-blue-600">
                        <FileText className="h-4 w-4 mr-2" />
                        View Client PO: {quotation.client_po_number}
                      </DropdownMenuItem>
                    )}
                    {/* Legacy: Convert to Contract option */}
                    {quotation.status === 'accepted' && (
                      <>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={() => openConvertDialog(quotation)}>
                          <ArrowRight className="h-4 w-4 mr-2" />
                          Convert to Contract (Legacy)
                        </DropdownMenuItem>
                      </>
                    )}
                    {/* Sent/Approved: Can create revision */}
                    {(quotation.status === 'draft' || quotation.status === 'sent' || quotation.status === 'approved' || quotation.status === 'sent_to_client') && (
                      <>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={() => handleRevise(quotation)}>
                          <Edit className="h-4 w-4 mr-2" />
                          Create Revision
                        </DropdownMenuItem>
                      </>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* View Quotation Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Quotation Details</DialogTitle>
          </DialogHeader>
          
          {selectedQuotation && (
            <div className="space-y-6">
              <div className="flex justify-between items-start">
                <div>
                  <h3 className="text-2xl font-bold">{selectedQuotation.quotation_number}</h3>
                  <p className="text-muted-foreground">{selectedQuotation.client_name}</p>
                  {selectedQuotation.client_email && (
                    <p className="text-sm text-muted-foreground">{selectedQuotation.client_email}</p>
                  )}
                </div>
                <Badge className={statusConfig[selectedQuotation.status]?.color}>
                  {statusConfig[selectedQuotation.status]?.label}
                </Badge>
              </div>

              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-muted-foreground">Valid Until</p>
                  <p className="font-medium">{new Date(selectedQuotation.valid_until).toLocaleDateString()}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Created</p>
                  <p className="font-medium">{new Date(selectedQuotation.created_at).toLocaleDateString()}</p>
                </div>
              </div>

              <div>
                <h4 className="font-semibold mb-2">Items</h4>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-2">Item</th>
                      <th className="text-center py-2">Qty</th>
                      <th className="text-right py-2">Rate</th>
                      <th className="text-right py-2">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedQuotation.items?.map((item, i) => (
                      <tr key={i} className="border-b">
                        <td className="py-2">
                          {item.asset_type}
                          {item.description && <span className="text-muted-foreground"> - {item.description}</span>}
                          <div className="flex gap-1 mt-1">
                            {item.is_from_own_fleet && <Badge variant="outline" className="text-xs">Own Fleet</Badge>}
                            {item.with_driver && <Badge variant="outline" className="text-xs">+Driver</Badge>}
                          </div>
                        </td>
                        <td className="text-center py-2">{item.quantity}</td>
                        <td className="text-right py-2">SAR {item.rate_per_unit?.toLocaleString()}</td>
                        <td className="text-right py-2">SAR {item.total?.toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="border-t pt-4">
                <div className="flex justify-end">
                  <div className="w-64 space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span>Subtotal:</span>
                      <span>SAR {selectedQuotation.subtotal?.toLocaleString()}</span>
                    </div>
                    {(selectedQuotation.mobilization_total || 0) > 0 && (
                      <div className="flex justify-between">
                        <span>Mobilization:</span>
                        <span>SAR {selectedQuotation.mobilization_total?.toLocaleString()}</span>
                      </div>
                    )}
                    <div className="flex justify-between">
                      <span>VAT ({selectedQuotation.vat_rate}%):</span>
                      <span>SAR {selectedQuotation.vat_amount?.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between font-bold text-lg border-t pt-2">
                      <span>Total:</span>
                      <span>SAR {selectedQuotation.total?.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between pt-2 border-t">
                      <span>Profit:</span>
                      <span className="text-green-600 font-semibold">
                        SAR {selectedQuotation.profit_amount?.toLocaleString()} ({selectedQuotation.profit_margin?.toFixed(1)}%)
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {selectedQuotation.notes && (
                <div>
                  <h4 className="font-semibold mb-1">Notes</h4>
                  <p className="text-sm text-muted-foreground">{selectedQuotation.notes}</p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Convert to Contract Dialog */}
      <Dialog open={convertDialogOpen} onOpenChange={setConvertDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Convert to Contract</DialogTitle>
          </DialogHeader>
          
          {selectedQuotation && (
            <div className="space-y-4">
              <p className="text-muted-foreground">
                Create a new contract from quotation <strong>{selectedQuotation.quotation_number}</strong> for {selectedQuotation.client_name}.
              </p>
              <p className="text-sm">Contract Value: <strong>SAR {selectedQuotation.total?.toLocaleString()}</strong></p>
              
              <div className="space-y-2">
                <Label>Contract Start Date *</Label>
                <Input
                  type="date"
                  value={contractStartDate}
                  onChange={(e) => setContractStartDate(e.target.value)}
                />
              </div>
              
              <div className="bg-blue-50 p-3 rounded-lg text-sm text-blue-800">
                <Truck className="h-4 w-4 inline mr-2" />
                Operations team will be notified to arrange mobilization.
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setConvertDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleConvertToContract}>Create Contract</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Client PO Dialog - NEW: Per spec, Client PO can only be created from Accepted Quotation */}
      <Dialog open={createPODialogOpen} onOpenChange={setCreatePODialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Create Client Purchase Order</DialogTitle>
          </DialogHeader>
          
          {selectedQuotation && (
            <div className="space-y-4">
              <div className="bg-emerald-50 border border-emerald-200 p-3 rounded-lg">
                <p className="text-emerald-800 text-sm">
                  Creating Client PO from accepted quotation <strong>{selectedQuotation.quotation_number}</strong>
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-muted-foreground">Client</p>
                  <p className="font-semibold">{selectedQuotation.client_name}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">PO Value</p>
                  <p className="font-semibold">SAR {selectedQuotation.total?.toLocaleString()}</p>
                </div>
              </div>
              
              <div className="space-y-2">
                <Label>Client PO Number *</Label>
                <Input
                  value={poNumber}
                  onChange={(e) => setPONumber(e.target.value)}
                  placeholder="Enter client's PO number"
                  data-testid="client-po-number-input"
                />
                <p className="text-xs text-muted-foreground">
                  Enter the PO number from the client&apos;s purchase order document
                </p>
              </div>
              
              <div className="bg-amber-50 p-3 rounded-lg text-sm text-amber-800">
                <AlertTriangle className="h-4 w-4 inline mr-2" />
                After creating the Client PO, you will need to upload the client&apos;s PO document before creating delivery notes.
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setCreatePODialogOpen(false)}>Cancel</Button>
            <Button onClick={handleCreateClientPO} data-testid="create-client-po-btn">
              <ShoppingCart className="h-4 w-4 mr-2" />
              Create Client PO
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Revise Quotation Dialog */}
      <Dialog open={reviseDialogOpen} onOpenChange={(open) => {
        if (!open) {
          setReviseDialogOpen(false);
          setReviseItems([]);
        }
      }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="revise-quotation-dialog">
          <DialogHeader>
            <DialogTitle>Revise Quotation</DialogTitle>
          </DialogHeader>
          
          {selectedQuotation && (
            <div className="space-y-4">
              <div className="bg-amber-50 border border-amber-200 p-3 rounded-lg">
                <p className="text-amber-800 text-sm">
                  Creating a revision of <strong>{selectedQuotation.quotation_number}</strong>. 
                  Update the rates below and submit to create a new revision.
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-muted-foreground">Client</p>
                  <p className="font-semibold">{selectedQuotation.client_name}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Current Total</p>
                  <p className="font-semibold">SAR {selectedQuotation.total?.toLocaleString()}</p>
                </div>
              </div>

              {/* Editable Line Items */}
              <div className="border rounded-lg p-4">
                <h4 className="font-semibold mb-3">Line Items</h4>
                <div className="space-y-4">
                  {reviseItems.map((item, index) => (
                    <div key={index} className="p-4 bg-slate-50 rounded-lg border" data-testid={`revise-item-${index}`}>
                      <div className="flex items-center justify-between mb-3">
                        <div>
                          <span className="font-semibold">{item.asset_type}</span>
                          {item.description && <span className="text-muted-foreground ml-2">- {item.description}</span>}
                        </div>
                        <div className="flex gap-2">
                          {item.is_from_own_fleet && <Badge variant="outline" className="text-xs">Own Fleet</Badge>}
                          {item.with_driver && <Badge variant="outline" className="text-xs">+Driver</Badge>}
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        <div>
                          <Label className="text-xs">Quantity</Label>
                          <Input
                            type="number"
                            min="1"
                            value={item.quantity}
                            onChange={(e) => updateReviseItem(index, 'quantity', parseInt(e.target.value) || 1)}
                            data-testid={`revise-qty-${index}`}
                          />
                        </div>
                        <div>
                          <Label className="text-xs">Supplier Cost</Label>
                          <Input
                            type="number"
                            value={item.supplier_cost}
                            onChange={(e) => updateReviseItem(index, 'supplier_cost', parseFloat(e.target.value) || 0)}
                            data-testid={`revise-supplier-cost-${index}`}
                          />
                        </div>
                        <div>
                          <Label className="text-xs">Rate per Unit (Monthly)</Label>
                          <Input
                            type="number"
                            value={item.rate_per_unit}
                            onChange={(e) => updateReviseItem(index, 'rate_per_unit', parseFloat(e.target.value) || 0)}
                            data-testid={`revise-rate-${index}`}
                          />
                        </div>
                        <div>
                          <Label className="text-xs">Mobilization</Label>
                          <Input
                            type="number"
                            value={item.mobilization_cost || 0}
                            onChange={(e) => updateReviseItem(index, 'mobilization_cost', parseFloat(e.target.value) || 0)}
                            data-testid={`revise-mob-${index}`}
                          />
                        </div>
                      </div>
                      
                      {item.with_driver && (
                        <div className="mt-3 w-1/4">
                          <Label className="text-xs">Driver Rate</Label>
                          <Input
                            type="number"
                            value={item.driver_rate || 0}
                            onChange={(e) => updateReviseItem(index, 'driver_rate', parseFloat(e.target.value) || 0)}
                            data-testid={`revise-driver-rate-${index}`}
                          />
                        </div>
                      )}

                      <div className="mt-3 flex justify-between items-center text-sm">
                        <span className="text-muted-foreground">
                          Line Total: <span className="font-semibold text-foreground">SAR {item.total?.toLocaleString()}</span>
                        </span>
                        {item.supplier_cost > 0 && item.rate_per_unit > 0 && (
                          <span className={((item.rate_per_unit - item.supplier_cost) / item.rate_per_unit * 100) >= 10 ? 'text-green-600' : 'text-red-600'}>
                            Margin: {((item.rate_per_unit - item.supplier_cost) / item.rate_per_unit * 100).toFixed(1)}%
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Summary */}
                <div className="mt-4 pt-4 border-t">
                  <div className="flex justify-between items-center">
                    <span className="text-muted-foreground">Revised Subtotal:</span>
                    <span className="font-bold text-lg">
                      SAR {reviseItems.reduce((sum, item) => sum + (item.total || 0), 0).toLocaleString()}
                    </span>
                  </div>
                  {(() => {
                    const revisedSubtotal = reviseItems.reduce((sum, item) => sum + (item.total || 0), 0);
                    const revisedMob = reviseItems.reduce((sum, item) => sum + (parseFloat(item.mobilization_cost) || 0), 0);
                    const revisedVat = (revisedSubtotal + revisedMob) * 0.15;
                    const revisedTotal = revisedSubtotal + revisedMob + revisedVat;
                    return (
                      <>
                        {revisedMob > 0 && (
                          <div className="flex justify-between text-sm">
                            <span className="text-muted-foreground">Mobilization:</span>
                            <span>SAR {revisedMob.toLocaleString()}</span>
                          </div>
                        )}
                        <div className="flex justify-between text-sm">
                          <span className="text-muted-foreground">VAT (15%):</span>
                          <span>SAR {revisedVat.toLocaleString()}</span>
                        </div>
                        <div className="flex justify-between font-bold text-lg border-t pt-2 mt-2">
                          <span>Revised Total:</span>
                          <span>SAR {revisedTotal.toLocaleString()}</span>
                        </div>
                      </>
                    );
                  })()}
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => {
                setReviseDialogOpen(false);
                setReviseItems([]);
              }}
              disabled={reviseSubmitting}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleReviseSubmit} 
              disabled={reviseSubmitting || reviseItems.length === 0}
              data-testid="submit-revision-btn"
            >
              {reviseSubmitting ? 'Creating Revision...' : 'Create Revision'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
